import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  List,
  ListItem,
  Divider,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  fetchSelectorExperience,
  updateSelectorExperience,
  fetchOrganizationResLinks,
} from "../../../redux/actions/selectorExperienceActions";
import { fetchOrganizations } from "../../../redux/actions/organizationActions";
import { ExperienceEntry } from "../../../redux/reducers/selectorExperienceReducer";

interface ResLink {
  id: string;
  display_name?: string | null;
  behavior_type: string;
}

const SelectorExperienceView: React.FC = () => {
  const { organizationId, selectorId } = useParams<{ organizationId: string; selectorId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { currentSelectorExperience, loading, error } = useSelector(
    (state: RootState) => state.selectorExperiences
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editedPreSelectionCopy, setEditedPreSelectionCopy] = useState("");
  const [editedSelectorTitle, setEditedSelectorTitle] = useState("");
  const [editedSelectorSubtitle, setEditedSelectorSubtitle] = useState("");
  const [editedSubClientModeEnabled, setEditedSubClientModeEnabled] = useState(false);
  const [editedEntries, setEditedEntries] = useState<ExperienceEntry[]>([]);
  const [resLinks, setResLinks] = useState<ResLink[]>([]);
  const [loadingResLinks, setLoadingResLinks] = useState(false);

  useEffect(() => {
    if (organizationId && selectorId) {
      dispatch(fetchSelectorExperience({ organizationId, selectorId }))
        .unwrap()
        .catch((error: any) => {
          if (error.status === 403) {
            toast.error("You don't have permission to view this selector experience for this organization");
          }
        });
    }
    dispatch(fetchOrganizations());
    
    // Fetch ResLinks for the organization
    if (organizationId) {
      setLoadingResLinks(true);
      dispatch(fetchOrganizationResLinks(organizationId))
        .unwrap()
        .then((links) => {
          setResLinks(links);
        })
        .catch((error: any) => {
          console.error("Failed to fetch ResLinks:", error);
          if (error.status === 403) {
            toast.error("You don't have permission to view resources for this organization");
          }
          setResLinks([]);
        })
        .finally(() => {
          setLoadingResLinks(false);
        });
    }
  }, [dispatch, organizationId, selectorId]);

  useEffect(() => {
    if (currentSelectorExperience) {
      setEditedPreSelectionCopy(currentSelectorExperience.pre_selection_copy || "");
      setEditedSelectorTitle(currentSelectorExperience.selector_title || "");
      setEditedSelectorSubtitle(currentSelectorExperience.selector_subtitle || "");
      setEditedSubClientModeEnabled(currentSelectorExperience.sub_client_mode_enabled || false);
      setEditedEntries(currentSelectorExperience.entries || []);
    }
  }, [currentSelectorExperience]);

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (currentSelectorExperience) {
      setEditedPreSelectionCopy(currentSelectorExperience.pre_selection_copy || "");
      setEditedSelectorTitle(currentSelectorExperience.selector_title || "");
      setEditedSelectorSubtitle(currentSelectorExperience.selector_subtitle || "");
      setEditedSubClientModeEnabled(currentSelectorExperience.sub_client_mode_enabled || false);
      setEditedEntries(currentSelectorExperience.entries || []);
    }
  };

  const handleSave = () => {
    if (!organizationId || !selectorId) {
      toast.error("Organization ID and Selector ID are required");
      return;
    }

    const updateData = {
      organization_id: organizationId,
      selector_id: selectorId,
      pre_selection_copy: editedPreSelectionCopy || null,
      selector_title: editedSelectorTitle || null,
      selector_subtitle: editedSelectorSubtitle || null,
      sub_client_mode_enabled: editedSubClientModeEnabled,
      entries: editedEntries,
    };

    dispatch(updateSelectorExperience(updateData))
      .unwrap()
      .then(() => {
        toast.success("Selector Experience updated successfully");
        setIsEditing(false);
      })
      .catch((error: any) => {
        if (error.status === 403) {
          toast.error("You don't have permission to update selector experiences for this organization");
        } else {
          toast.error(`Failed to update selector experience: ${error.message || 'Unknown error'}`);
        }
      });
  };

  const handleAddEntry = () => {
    setEditedEntries([
      ...editedEntries,
      { title: "", subtitle: null, res_link_params_id: "" },
    ]);
  };

  const handleRemoveEntry = (index: number) => {
    setEditedEntries(editedEntries.filter((_, i) => i !== index));
  };

  const handleEntryChange = (
    index: number,
    field: keyof ExperienceEntry,
    value: string
  ) => {
    const newEntries = [...editedEntries];
    newEntries[index] = {
      ...newEntries[index],
      [field]: value || null,
    };
    setEditedEntries(newEntries);
  };

  const handleMoveEntryUp = (index: number) => {
    if (index === 0) return;
    const newEntries = [...editedEntries];
    [newEntries[index - 1], newEntries[index]] = [newEntries[index], newEntries[index - 1]];
    setEditedEntries(newEntries);
  };

  const handleMoveEntryDown = (index: number) => {
    if (index === editedEntries.length - 1) return;
    const newEntries = [...editedEntries];
    [newEntries[index], newEntries[index + 1]] = [newEntries[index + 1], newEntries[index]];
    setEditedEntries(newEntries);
  };

  const handleResLinkSelect = (index: number, value: ResLink | string | null) => {
    if (value) {
      const newEntries = [...editedEntries];
      newEntries[index] = {
        ...newEntries[index],
        res_link_params_id: typeof value === 'string' ? value : value.id,
      };
      setEditedEntries(newEntries);
    }
  };

  const getResLinkDisplayName = (option: ResLink | string) => {
    if (typeof option === 'string') {
      return option;
    }
    if (option.display_name) {
      return `${option.display_name} (${option.id})`;
    }
    return option.id;
  };

  const getResLinkDisplayForView = (resLinkId: string) => {
    const resLink = resLinks.find((rl) => rl.id === resLinkId);
    if (resLink?.display_name) {
      return `${resLink.display_name} (${resLinkId})`;
    }
    return resLinkId;
  };

  if (loading && !currentSelectorExperience) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!currentSelectorExperience) {
    return <div>Selector Experience not found</div>;
  }

  return (
    <Box sx={{ p: 4 }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Typography variant="h5" gutterBottom>
        Selector Experience Details
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <TextField
          label="Selector ID"
          value={currentSelectorExperience.selector_id}
          fullWidth
          margin="normal"
          required
          helperText="Globally unique selector identifier (e.g., 'solana', 'doctor-testbed'). This field cannot be changed after creation."
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Organization ID"
          value={currentSelectorExperience.organization_id}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Pre-Selection Copy"
          value={
            isEditing
              ? editedPreSelectionCopy
              : currentSelectorExperience.pre_selection_copy || ""
          }
          onChange={(e) => setEditedPreSelectionCopy(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={3}
          helperText="Text shown before selector options. Leave empty to skip the initial screen and go directly to selector options."
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Selector Title"
          value={
            isEditing
              ? editedSelectorTitle
              : currentSelectorExperience.selector_title || ""
          }
          onChange={(e) => setEditedSelectorTitle(e.target.value)}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Selector Subtitle"
          value={
            isEditing
              ? editedSelectorSubtitle
              : currentSelectorExperience.selector_subtitle || ""
          }
          onChange={(e) => setEditedSelectorSubtitle(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={2}
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isEditing ? editedSubClientModeEnabled : currentSelectorExperience.sub_client_mode_enabled}
                onChange={(e) => setEditedSubClientModeEnabled(e.target.checked)}
                disabled={!isEditing}
              />
            }
            label="Sub-Client Mode Enabled"
          />
          <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
            Enable sub-client mode for this selector experience
          </Typography>
        </Box>

        {/* Entries Section */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Entries</Typography>
            {isEditing && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddEntry}
                size="small"
              >
                Add Entry
              </Button>
            )}
          </Box>
          {editedEntries.length > 0 ? (
            <List>
              {editedEntries.map((entry, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      display: "block",
                      backgroundColor: isEditing ? "#fafafa" : "transparent",
                      mb: 2,
                      p: 2,
                      border: isEditing ? "1px solid #e0e0e0" : "none",
                      borderRadius: 1,
                    }}
                  >
                    {isEditing ? (
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2">Entry {index + 1}</Typography>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveEntryUp(index)}
                              disabled={index === 0}
                            >
                              <ArrowUpwardIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveEntryDown(index)}
                              disabled={index === editedEntries.length - 1}
                            >
                              <ArrowDownwardIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveEntry(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        <TextField
                          label="Title"
                          value={entry.title}
                          onChange={(e) => handleEntryChange(index, "title", e.target.value)}
                          fullWidth
                          margin="dense"
                          required
                        />
                        <TextField
                          label="Subtitle"
                          value={entry.subtitle || ""}
                          onChange={(e) => handleEntryChange(index, "subtitle", e.target.value)}
                          fullWidth
                          margin="dense"
                        />
                        
                        {/* ResLink Picker with Autocomplete */}
                        <Autocomplete
                          options={resLinks}
                          getOptionLabel={(option) => getResLinkDisplayName(option)}
                          loading={loadingResLinks}
                          value={resLinks.find((rl) => rl.id === entry.res_link_params_id) || null}
                          onChange={(_, newValue) => handleResLinkSelect(index, newValue)}
                          freeSolo
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="ResLink"
                              margin="dense"
                              required
                              helperText="Select a ResLink or type the ID directly"
                              value={entry.res_link_params_id}
                              onChange={(e) =>
                                handleEntryChange(index, "res_link_params_id", e.target.value)
                              }
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingResLinks ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {entry.title}
                        </Typography>
                        {entry.subtitle && (
                          <Typography variant="body2" color="text.secondary">
                            {entry.subtitle}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.disabled">
                          ResLink: {getResLinkDisplayForView(entry.res_link_params_id)}
                        </Typography>
                      </Box>
                    )}
                  </ListItem>
                  {index < editedEntries.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
              No entries configured
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" marginTop={3} gap={2}>
          {!isEditing ? (
            <>
              <Button variant="contained" color="primary" onClick={handleEdit}>
                Edit
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => navigate("/dashboard/selector-experiences")}
              >
                Back
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SelectorExperienceView;
